import { Product } from '../../home/models/product.model';
import {
  BranchApi,
  CategoryApi,
  PricingApi,
  ProductApi,
  ProductDescriptionApi,
  ProductImageApi,
  ProductItemApi,
  ProductStyleApi,
  SizeApi,
  SubCategoryApi,
} from '../models/catalog-api.model';
import { Category } from '../models/category.model';
import {
  ProductDetail,
  ProductSection,
  ProductStyleOption,
  ProductVariant,
} from '../models/product-detail.model';

// Funciones puras: sin inyección de Angular, para poder testearlas sin TestBed.

export const NEW_PRODUCT_DAYS = 15;

export const PRODUCT_PLACEHOLDER_IMAGE = '/assets/product-placeholder.svg';

export const CATEGORY_PLACEHOLDER_IMAGE = '/assets/product-placeholder.svg';

/**
 * El backend de categorías (dm-catalog-ms) todavía no expone una imagen por categoría,
 * así que mientras tanto se resuelve por nombre contra los assets locales ya existentes
 * (normalizado: minúsculas y sin tildes, para que "Juguetería" matchee "jugueteria").
 */
const CATEGORY_IMAGE_BY_NAME: Record<string, string> = {
  camisetas: 'assets/camisetas-tienda.png',
  ropa: 'assets/ropa-tienda.png',
  accesorios: 'assets/accesorios-tienda.png',
  balones: 'assets/balones-tienda.png',
  souvenir: 'assets/souvenir-tienda.png',
  jugueteria: 'assets/juguetes-tienda.png',
};

function normalizeCategoryName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

export function toCategory(category: CategoryApi): Category {
  return {
    id: String(category.id),
    name: category.name,
    imageUrl: CATEGORY_IMAGE_BY_NAME[normalizeCategoryName(category.name)] ?? CATEGORY_PLACEHOLDER_IMAGE,
  };
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

type ActiveProductImage = ProductImageApi & { url: string };

/**
 * Un product es "nuevo" si su antigüedad es menor a NEW_PRODUCT_DAYS días.
 * Límite exclusivo: a los 15 días exactos deja de ser "nuevo" (día 15 no cuenta,
 * solo cuentan los primeros 14 días completos + fracción). Fechas futuras o
 * inválidas nunca son "nuevo".
 */
export function isNewProduct(createdAt: string, now: Date): boolean {
  const createdDate = new Date(createdAt);
  if (Number.isNaN(createdDate.getTime())) {
    return false;
  }
  const ageMs = now.getTime() - createdDate.getTime();
  if (ageMs < 0) {
    return false;
  }
  return ageMs / MS_PER_DAY < NEW_PRODUCT_DAYS;
}

/**
 * Precio de venta de una unidad. Sin pricing (lecturas que no cotizan) se cobra el de lista.
 * Con promotion caído el backend ya manda salePrice == listPrice, así que no hace falta mirar
 * discountChecked: la UI muestra el precio normal, sin tachado.
 */
export function resolveSalePrice(pricing: PricingApi | undefined, listPrice: number): number {
  return pricing?.salePrice ?? listPrice;
}

export function toProductCard(product: ProductApi, now: Date): Product {
  return {
    id: product.id,
    title: product.name,
    price: product.price,
    salePrice: resolveSalePrice(product.pricing, product.price),
    imageUrl: product.imageUrl ?? PRODUCT_PLACEHOLDER_IMAGE,
    isNew: isNewProduct(product.createdAt, now),
    productBranchId: product.productBranchId,
  };
}

/**
 * Resuelve los branchId que caen bajo una categoría, bajando por la jerarquía
 * categoría -> subcategoría -> rama (no hay un categoryId directo en la rama ni en el producto).
 */
export function resolveBranchIdsForCategory(
  categoryId: number,
  subCategories: SubCategoryApi[],
  branches: BranchApi[],
): Set<number> {
  const subCategoryIds = new Set(
    subCategories.filter((subCategory) => subCategory.categoryId === categoryId).map((s) => s.id),
  );
  return new Set(
    branches.filter((branch) => subCategoryIds.has(branch.subCategoryId)).map((b) => b.id),
  );
}

/** `categoryId` null u omitido devuelve todos los productos ("TODAS"). */
export function filterProductsByCategory(
  products: Product[],
  categoryId: number | null,
  subCategories: SubCategoryApi[],
  branches: BranchApi[],
): Product[] {
  if (categoryId === null) {
    return products;
  }
  const branchIds = resolveBranchIdsForCategory(categoryId, subCategories, branches);
  return products.filter(
    (product) => product.productBranchId !== undefined && branchIds.has(product.productBranchId),
  );
}

/** Primer estilo activo por sortOrder (nulls al final); empate → id más bajo. */
export function pickDefaultStyle(styles: ProductStyleApi[]): ProductStyleApi | null {
  return sortActiveStyles(styles)[0] ?? null;
}

export function toProductDetail(
  product: ProductApi,
  styles: ProductStyleApi[],
  style: ProductStyleApi | null,
  images: ProductImageApi[],
  items: ProductItemApi[],
  sizes: SizeApi[],
): ProductDetail {
  const activeImages = style ? selectActiveImages(images) : [];
  const mainImage = resolveMainImage(activeImages, product);
  const variants = style ? buildVariants(items, style.id, sizes) : [];

  return {
    id: product.id,
    title: product.name,
    precio: product.price,
    descripcion: resolveDescription(product),
    imagenPrincipal: mainImage,
    imagenCarrito: resolveCartImage(activeImages, mainImage),
    imagenes: style ? buildGallery(activeImages) : [],
    tallas: variants.map((variant) => variant.size),
    styleId: style?.id ?? null,
    styles: buildStyleOptions(styles),
    variants,
    sections: buildSections(product.descriptions),
  };
}

function resolveDescription(product: ProductApi): string {
  return product.description ?? product.shortDescription ?? '';
}

function compareBySortOrderThenId(a: ProductStyleApi, b: ProductStyleApi): number {
  const orderA = a.sortOrder ?? Number.POSITIVE_INFINITY;
  const orderB = b.sortOrder ?? Number.POSITIVE_INFINITY;
  return orderA !== orderB ? orderA - orderB : a.id - b.id;
}

function sortActiveStyles(styles: ProductStyleApi[]): ProductStyleApi[] {
  return styles.filter((style) => style.isActive).sort(compareBySortOrderThenId);
}

function buildStyleOptions(styles: ProductStyleApi[]): ProductStyleOption[] {
  return sortActiveStyles(styles).map((style) => ({
    id: style.id,
    colorId: style.colorId,
    name: style.name,
  }));
}

function isActiveWithUrl(image: ProductImageApi): image is ActiveProductImage {
  return image.isActive && image.url !== null;
}

function selectActiveImages(images: ProductImageApi[]): ActiveProductImage[] {
  return images.filter(isActiveWithUrl);
}

function compareByPositionThenId(a: ProductImageApi, b: ProductImageApi): number {
  const posA = a.position ?? Number.POSITIVE_INFINITY;
  const posB = b.position ?? Number.POSITIVE_INFINITY;
  return posA !== posB ? posA - posB : a.id - b.id;
}

function resolveMainImage(images: ActiveProductImage[], product: ProductApi): string {
  const primary = images.find((image) => image.typeImage === 'PRIMARY');
  if (primary) {
    return primary.url;
  }
  const [first] = [...images].sort(compareByPositionThenId);
  return first?.url ?? product.imageUrl ?? PRODUCT_PLACEHOLDER_IMAGE;
}

function resolveCartImage(images: ActiveProductImage[], mainImage: string): string {
  const thumbnail = images.find((image) => image.typeImage === 'THUMBNAIL');
  return thumbnail?.url ?? mainImage;
}

/**
 * Galería: PRIMARY primero (el backend garantiza como máximo una activa por estilo),
 * después el resto ordenado por posición. Las imágenes con typeImage null se
 * incluyen junto al resto: el backend no las tipó, pero siguen siendo fotos de
 * product válidas y ocultarlas perdería contenido sin necesidad. THUMBNAIL y ZOOM
 * quedan afuera porque son variantes técnicas de otra imagen, no fotos adicionales.
 */
function buildGallery(images: ActiveProductImage[]): string[] {
  const primary = images.filter((image) => image.typeImage === 'PRIMARY');
  const others = images
    .filter((image) => image.typeImage === 'GALLERY' || image.typeImage === null)
    .sort(compareByPositionThenId);
  return [...primary, ...others].map((image) => image.url);
}

function resolveSizeLabel(item: ProductItemApi, size: SizeApi | undefined): string {
  return size?.name ?? item.title ?? String(item.sizeId);
}

/** Los tamaños no tienen campo de orden propio; se ordena por sizeId ascendente. */
function buildVariants(
  items: ProductItemApi[],
  styleId: number,
  sizes: SizeApi[],
): ProductVariant[] {
  const sizeById = new Map(sizes.map((size) => [size.id, size]));
  return items
    .filter((item) => item.isActive && item.productStyleId === styleId)
    .sort((a, b) => a.sizeId - b.sizeId)
    .map((item) => ({
      productItemId: item.id,
      sizeId: item.sizeId,
      size: resolveSizeLabel(item, sizeById.get(item.sizeId)),
      price: item.price,
      salePrice: resolveSalePrice(item.pricing, item.price),
    }));
}

function hasText(description: ProductDescriptionApi): description is ProductDescriptionApi & {
  description: string;
} {
  return description.description !== null && description.description.trim().length > 0;
}

function buildSections(descriptions: ProductDescriptionApi[] | null): ProductSection[] {
  return (descriptions ?? []).filter(hasText).map((description) => ({
    title: description.title ?? '',
    text: description.description,
  }));
}
